import { DateTime } from 'luxon';
import { i18n } from '@src/i18n';
import type { TFunction } from 'i18next';

/**
 * Fallback tz for the bootstrap window where the working-site selector
 * hasn't hydrated yet (or app-level dates that aren't bound to a specific
 * project context — e.g. mock fixtures). JST because d-orca is a JP
 * product. Project-context formatters require an explicit tz at the call
 * site; this constant exists for call sites that pass
 * `useSelector(selectSiteTz) ?? APP_DEFAULT_TZ`.
 */
export const APP_DEFAULT_TZ = 'Asia/Tokyo';

export type Lang = 'en' | 'ja';

/**
 * Snapshot of the active i18n language as a discriminated `Lang`. Exposed
 * for callers that need a default — formatters themselves require `lang`
 * explicitly so the language dependency is visible at the call site.
 */
export function currentLang(): Lang {
  return i18n.language === 'ja' ? 'ja' : 'en';
}

const ORDINAL_PR = new Intl.PluralRules('en-US', { type: 'ordinal' });
const ORDINAL_SUFFIX: Record<Intl.LDMLPluralRule, string> = {
  zero: 'th',
  one: 'st',
  two: 'nd',
  few: 'rd',
  many: 'th',
  other: 'th',
};

function englishOrdinal(day: number): string {
  return `${day}${ORDINAL_SUFFIX[ORDINAL_PR.select(day)]}`;
}

/** Matches a bare YYYY-MM-DD with no time component. */
const YMD_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Convert any time-ish input into a DateTime anchored to `tz`.
 *
 * For ISO strings with a time component (or a `Z` / offset), the returned
 * DateTime represents the same UTC instant in `tz`. For bare YYYY-MM-DD
 * strings, the DateTime is constructed *in* `tz` (midnight that day, in
 * `tz`) — without this special case, `DateTime.fromISO('2026-04-08')`
 * yields system-local midnight and `setZone(...)` shifts the day for any
 * tz that differs from the system tz.
 */
function toZone(input: string | Date | DateTime, tz: string): DateTime {
  if (input instanceof DateTime) return input.setZone(tz);
  if (typeof input === 'string') {
    if (YMD_ONLY.test(input)) {
      return DateTime.fromISO(input, { zone: tz });
    }
    return DateTime.fromISO(input).setZone(tz);
  }
  return DateTime.fromJSDate(input).setZone(tz);
}

export function formatLongDate(
  input: string | Date | DateTime,
  lang: Lang,
  tz: string,
): string {
  const dt = toZone(input, tz);
  if (lang === 'ja') {
    return dt.setLocale('ja').toLocaleString({ year: 'numeric', month: 'long', day: 'numeric' });
  }
  const month = dt.setLocale('en').toFormat('MMMM');
  const year = dt.year;
  return `${month} ${englishOrdinal(dt.day)}, ${year}`;
}

/**
 * Compact `month day` calendar date (e.g. "May 30" / "5月30日") anchored to
 * `tz`. Year-less by design — for at-a-glance dashboard labels (certification
 * renewal dates, etc.) where the year is implied. `tz` is required for the same
 * reason as the other formatters: the calendar day depends on it.
 */
export function formatShortDate(
  input: string | Date | DateTime,
  lang: Lang,
  tz: string,
): string {
  return toZone(input, tz)
    .setLocale(lang)
    .toLocaleString({ month: 'short', day: 'numeric' });
}

/**
 * Abbreviated-month calendar date with the year (e.g. "Jun 12, 2026" /
 * "2026年6月12日") anchored to `tz` — for compact labels that span years, such
 * as a timeline's start and end.
 */
export function formatMediumDate(
  input: string | Date | DateTime,
  lang: Lang,
  tz: string,
): string {
  return toZone(input, tz)
    .setLocale(lang)
    .toLocaleString({ year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatTime(
  input: string | Date | DateTime,
  lang: Lang,
  tz: string,
): string {
  const dt = toZone(input, tz);
  if (lang === 'ja') return dt.setLocale('ja').toFormat('HH:mm');
  return dt.setLocale('en').toFormat('h:mm a');
}

export function formatTimeAgo(
  input: string | Date | DateTime,
  t: TFunction,
  lang: Lang,
  tz: string,
): string {
  const dt = toZone(input, tz);
  const now = DateTime.now().setZone(tz);
  const time = formatTime(dt, lang, tz);
  if (dt.hasSame(now, 'day')) return t('common.dates.todayAt', { time });
  if (dt.hasSame(now.minus({ days: 1 }), 'day')) return t('common.dates.yesterdayAt', { time });
  return formatLongDate(dt, lang, tz);
}

export function formatDateRange(
  from: string | Date | DateTime,
  to: string | Date | DateTime,
  lang: Lang,
  tz: string,
): string {
  return `${formatLongDate(from, lang, tz)} - ${formatLongDate(to, lang, tz)}`;
}

/**
 * `YYYY-MM-DD` calendar-date string in `tz`. tz is required because the
 * calendar day depends on it — formatting an instant near midnight in two
 * different zones yields two different days.
 */
export function formatYmd(
  input: string | Date | DateTime,
  tz: string,
): string {
  return toZone(input, tz).toFormat('yyyy-MM-dd');
}

/**
 * Compact `HH:mm-HH:mm` time range. `tz` is required because every caller of
 * this formatter renders a project-relative time (Daily Route arrivals /
 * departures) — there is no app-level "default tz" reading that would be
 * meaningful here, and requiring tz makes the project-context dependency
 * visible at the call site.
 */
export function formatTimeRange(
  arrival: string | Date | DateTime,
  departure: string | Date | DateTime,
  tz: string,
): string {
  const a = toZone(arrival, tz).toFormat('HH:mm');
  const d = toZone(departure, tz).toFormat('HH:mm');
  return `${a}-${d}`;
}

/**
 * Renders a duration in minutes as `HH:mm Hours` once it crosses an hour,
 * otherwise as `N mins`. Backed by the i18n `reports.dailyRoute.duration.*`
 * keys so the trailing label localises with the active language.
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return i18n.t('reports.dailyRoute.duration.mins', { m: minutes });
  }
  const hh = Math.floor(minutes / 60).toString().padStart(2, '0');
  const mm = (minutes % 60).toString().padStart(2, '0');
  return i18n.t('reports.dailyRoute.duration.hours', { hh, mm });
}

export { pickerDateToProjectNoonMs, projectDayAsPickerDate } from '@d-orca/shared/client/utils';

/**
 * Time-bearing sibling of `pickerDateToProjectNoonMs`. The DateTimeField
 * follows the same tz-naive convention: its emitted Date carries the picked
 * y/m/d/h/m in *local* fields, by convention representing the project-tz
 * calendar fields. Re-anchor those fields in the supplied project tz to get
 * the canonical epoch instant for submission.
 *
 * Use for any analytics card that emits a precise instant (incident-report)
 * rather than a calendar day (daily-route).
 */
export function pickerDateTimeToProjectMs(picked: Date, tz: string): number {
  return DateTime.fromObject(
    {
      year: picked.getFullYear(),
      month: picked.getMonth() + 1,
      day: picked.getDate(),
      hour: picked.getHours(),
      minute: picked.getMinutes(),
    },
    { zone: tz },
  ).toMillis();
}

/**
 * "Now" in the project tz, expressed as a tz-naive JS Date whose *local*
 * fields hold the project-tz y/m/d/h/m. Inverse of
 * `pickerDateTimeToProjectMs`: pre-fills a DateTimeField so the trigger
 * shows the project's current instant, and `pickerDateTimeToProjectMs`
 * round-trips back to the same epoch on submit.
 */
export function nowInProjectAsLocal(tz: string): Date {
  const now = DateTime.now().setZone(tz);
  return new Date(now.year, now.month - 1, now.day, now.hour, now.minute, 0, 0);
}

/**
 * Compact `yyyy-MM-dd HH:mm` wall-clock stamp in `APP_DEFAULT_TZ` (JST). Used
 * for app-level audit-style timestamps that aren't bound to a project tz —
 * e.g. "site synced at" on the sync page. Language-neutral on purpose (pure
 * numeric, no localised month names), so it reads the same in either locale.
 *
 * Returns the raw input unchanged when it can't be parsed as a DateTime, so a
 * surprise non-ISO value renders verbatim rather than as `Invalid DateTime`.
 */
export function formatJstShort(iso: string): string {
  const dt = DateTime.fromISO(iso).setZone(APP_DEFAULT_TZ);
  if (!dt.isValid) return iso;
  return dt.toFormat('yyyy-MM-dd HH:mm');
}

/**
 * Format a decimal-hours count as zero-padded `HH:MM`. Mirrors Trusstor's
 * `getHoursMinutesHoursFormatFromHours` — the Worker Timesheet report
 * surfaces both KPI totals and per-row "Total Hours on Site" values in
 * this exact shape (`23:52`, not `23h 52m`). Hours never wrap past 24 so
 * a worker totalling more than a day still reads as e.g. `27:15`.
 *
 * `Number.isFinite` / `< 0` guard collapses bad input to `00:00` so the
 * table grid never renders an empty cell from a math edge case.
 */
export function formatHoursAsHHMM(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '00:00';
  const totalMinutes = hours * 60;
  const hh = Math.floor(totalMinutes / 60);
  const mm = Math.floor(totalMinutes % 60);
  return `${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}`;
}

/**
 * Compact relative "x minutes/hours/days ago" via Intl.RelativeTimeFormat —
 * backs the Portfolio header's "last synced" label.
 * Local-clock arithmetic only; pair with `useNow` so the label stays current.
 */
export function formatAgo(iso: string, now: Date, locale: string): string {
  const ms = Math.max(0, now.getTime() - new Date(iso).getTime());
  const mins = Math.floor(ms / 60_000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  if (mins < 1) return rtf.format(0, 'second');
  if (mins < 60) return rtf.format(-mins, 'minute');
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return rtf.format(-hrs, 'hour');
  const days = Math.floor(hrs / 24);
  return rtf.format(-days, 'day');
}
